import React, { useState, useEffect } from 'react';

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const videoIdMatch = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (videoIdMatch && videoIdMatch[1]) {
    return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
  }
  return null;
};

const GameDetailsGrid = ({ game }) => {
  const renderPlaytime = () => {
    if (!game) return 'N/A';
    const { playing_time, combined_min_playtime, combined_max_playtime } = game;

    if (combined_min_playtime && combined_max_playtime) {
      if (combined_min_playtime === combined_max_playtime) {
        return `${combined_min_playtime} min`;
      }
      return `${combined_min_playtime} - ${combined_max_playtime} min`;
    }

    return `${playing_time || 'N/A'} min`;
  };

  const renderPlayerCount = () => {
    if (!game) return 'N/A';
    const { min_players, max_players, combined_min_players, combined_max_players } = game;
    const min = combined_min_players ?? min_players;
    const max = combined_max_players ?? max_players;

    if (min === max) {
      return `${min || 'N/A'}`;
    }
    return `${min || 'N/A'}-${max || 'N/A'}`;
  };

  return (
    <div className="game-details-grid">
      <img src={game.image_url || undefined} alt={`Box art for ${game.title}`} style={{ width: '100%', borderRadius: 'var(--pico-border-radius)' }} />
      <div>
        <p><strong>Year:</strong> {game.year_published || 'N/A'}</p>
        <p><strong>Players:</strong> {renderPlayerCount()}
          {game.bgg_data?.bgg_best_players && <small> (Best: {game.bgg_data.bgg_best_players})</small>}
        </p>
        {game.bgg_data?.bgg_recommended_players && (
          <p><small><strong>Recommended:</strong> {game.bgg_data.bgg_recommended_players}</small></p>
        )}
        <p><strong>Playing Time:</strong> {renderPlaytime()}</p>
        {game.bgg_data?.bgg_recommended_age > 0 && (
          <p><strong>Age:</strong> {game.bgg_data.bgg_recommended_age}+</p>
        )}
        <p><strong>BGG Rank:</strong> {game.bgg_rank || 'N/A'}</p>
        {game.bgg_data?.bgg_average_rating > 0 && (
          <p><strong>BGG Rating:</strong> {game.bgg_data.bgg_average_rating.toFixed(1)}</p>
        )}
        {game.bgg_data?.bgg_average_weight > 0 && (
          <p><strong>Weight:</strong> {game.bgg_data.bgg_average_weight.toFixed(2)} / 5</p>
        )}
      </div>
    </div>
  );
};

const GameCategoryTags = ({ categories, onCategoryClick }) => {
  if (!categories?.length) return null;

  return (
    <div>
      <strong>Categories:</strong>
      <div className="category-tags">
        {categories.map(category => (
          <a
            href="#"
            key={category}
            className="category-tag"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCategoryClick(category); }}
            title={`Filter by ${category}`}
          >
            {category}
          </a>
        ))}
      </div>
    </div>
  );
};

const GameDescription = ({ description }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!description) return null;

  return (
    <>
      <div
        className={`description-content ${!isExpanded ? 'collapsed' : ''}`}
        dangerouslySetInnerHTML={{ __html: description }}
      />
      <button className="read-more-button" onClick={() => setIsExpanded(e => !e)}>
        {isExpanded ? 'Show less' : 'Show more...'}
      </button>
    </>
  );
};

const GameVideoSection = ({ title, videoUrl }) => {
  const embedUrl = getYouTubeEmbedUrl(videoUrl);
  if (!embedUrl) return null;

  return (
    <>
      <hr />
      <h5>{title}</h5>
      <div className="video-container">
        <iframe
          src={embedUrl}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={title}
        ></iframe>
      </div>
    </>
  );
};

const GameExpansionList = ({ expansions, onExpansionClick }) => {
  if (!expansions?.length) return null;

  return (
    <>
      <hr />
      <h5>Expansions</h5>
      <ul className="expansion-list">
        {expansions.map(exp => (
          <li key={exp.id}>
            <a href="#" onClick={(e) => { e.preventDefault(); onExpansionClick(exp.id); }}>{exp.title}</a>
          </li>
        ))}
      </ul>
    </>
  );
};

const GameModalFooter = ({ game, onEditClick }) => (
  <footer className="modal-footer">
    {onEditClick && (
      <button onClick={() => onEditClick(game.id)} role="button" className="contrast">Edit</button>
    )}
    {game.rules_url && (
      <a href={game.rules_url} target="_blank" rel="noopener noreferrer" role="button" className="secondary">Rules</a>
    )}
    {game.bgg_id && (
      <a href={`https://boardgamegeek.com/boardgame/${game.bgg_id}`} target="_blank" rel="noopener noreferrer" role="button">View on BGG</a>
    )}
  </footer>
);

const GameModal = ({ gameId, onClose, onCategoryClick, onBackToGroup, onEditClick }) => {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([gameId]);

  const currentId = navigationHistory[navigationHistory.length - 1];

  useEffect(() => {
    const fetchGameDetails = async () => {
      if (!currentId) return;

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/games/${currentId}.json`);
        if (!response.ok) throw new Error('Game not found');
        const data = await response.json();
        setGame(data);
      } catch (err) {
        console.error('Failed to fetch game details:', err);
        setError(err.message);
        setGame(null);
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetails();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [currentId]);

  const handleExpansionClick = (expansionId) => {
    setNavigationHistory(prev => [...prev, expansionId]);
  };

  const handleBackClick = () => {
    setNavigationHistory(prev => prev.slice(0, -1));
  };

  const handleCategoryTagClick = (category) => {
    onCategoryClick(category);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <article className="modal-content" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          {loading ? (
            <h2 aria-busy="true">Loading...</h2>
          ) : (
            <h2>{game?.title}</h2>
          )}
          {(navigationHistory.length > 1 || onBackToGroup) && (
            <button onClick={navigationHistory.length > 1 ? handleBackClick : onBackToGroup} className="back-button" aria-label="Go back">
              &larr;
            </button>
          )}
          <button aria-label="Close" className="close-button" onClick={onClose}>&times;</button>
        </header>

        {loading ? (
          <div aria-busy="true" />
        ) : error ? (
          <p>Error: {error}</p>
        ) : !game ? (
          <p>Could not load game details.</p>
        ) : (
          <>
            <GameDetailsGrid game={game} />
            <GameCategoryTags categories={game.categories} onCategoryClick={handleCategoryTagClick} />
            <hr />
            <GameDescription description={game.description} />
            <GameVideoSection title="Rules Video" videoUrl={game.rules_video_url} />
            <GameVideoSection title="Playthrough Video" videoUrl={game.playthrough_video_url} />
            <GameExpansionList expansions={game.expansions} onExpansionClick={handleExpansionClick} />
            <GameModalFooter game={game} onEditClick={onEditClick} />
          </>
        )}
      </article>
    </div>
  );
};

export default GameModal;